import * as Y from "yjs"
import { WebsocketProvider } from "y-websocket"
import { getApiBaseUrl } from "~/lib/client-url"

/*
 * Standard Yjs WebSocket provider (y-websocket) against Orbit's own
 * /api/collab/:kind/:id endpoint. The server owns the authoritative document,
 * persists committed state, and fans updates out through Redis; edits are
 * applied locally immediately and synced through the server.
 */
export type EditorCollabRoom = {
	kind: "issue" | "document" | "form"
	id: string
	refs: number
	doc: Y.Doc
	provider: WebsocketProvider
	fragment: Y.XmlFragment
}

const rooms = new Map<string, EditorCollabRoom>()

const roomKey = (kind: "issue" | "document" | "form", id: string) =>
	`${kind}:${id}`

export function acquireCollabRoom({
	kind,
	id
}: {
	kind: "issue" | "document" | "form"
	id: string
}) {
	const key = roomKey(kind, id)
	const existing = rooms.get(key)
	if (existing) {
		existing.refs++
		if (!existing.provider.shouldConnect) existing.provider.connect()
		return existing
	}
	const doc = new Y.Doc()
	const url = new URL("/api/collab", getApiBaseUrl())
	url.protocol = url.protocol === "https:" ? "wss:" : "ws:"
	// y-websocket strips the trailing slash and appends "/roomname"; this
	// yields the documented /api/collab/:kind/:id endpoint.
	// Periodic state re-sync repairs missed relay updates (e.g. after a rolling
	// prod deploy); backoff is capped above the library default so long deploy
	// windows do not hammer the server while still recovering within seconds.
	const provider = new WebsocketProvider(url.href, `${kind}/${id}`, doc, {
		resyncInterval: 15_000,
		maxBackoffTime: 10_000
	})
	if (import.meta.env.DEV) {
		// Vite relays browser console errors into the dev log.
		provider.on("status", (event: { status: string }) =>
			console.error(`[collab-client] status ${event.status} ${key}`)
		)
		provider.on("sync", (synced: boolean) =>
			console.error(
				`[collab-client] synced ${synced} ${key} docLen ${doc.getXmlFragment("document").length}`
			)
		)
		provider.on("connection-error", () =>
			console.error(`[collab-client] connection-error ${key}`)
		)
		provider.on("connection-close", (event: CloseEvent | null) =>
			console.error(
				`[collab-client] connection-close code ${event?.code ?? "?"} ${key}`
			)
		)
		doc.on("update", (update: Uint8Array, origin: unknown) =>
			console.error(
				`[collab-client] doc update len ${update.length} origin ${String(origin).slice(0, 40)} key ${key}`
			)
		)
	}
	const connection: EditorCollabRoom = {
		kind,
		id,
		refs: 1,
		doc,
		provider,
		fragment: doc.getXmlFragment("document")
	}
	rooms.set(key, connection)
	return connection
}

export function releaseCollabRoom(
	kind: "issue" | "document" | "form",
	id: string
) {
	const key = roomKey(kind, id)
	const connection = rooms.get(key)
	if (!connection) return
	connection.refs = Math.max(0, connection.refs - 1)
	// StrictMode replays effects before this runs; acquire belongs to an effect,
	// never render. Multiple panes share a single websocket provider per room.
	queueMicrotask(() => {
		if (
			connection.refs !== 0 ||
			connection.provider.ws !== null ||
			!connection.provider.synced ||
			rooms.get(key) !== connection
		)
			return
		rooms.delete(key)
		connection.provider.destroy()
		connection.doc.destroy()
	})
}

const resumeRooms = () => {
	if (document.visibilityState === "hidden" || !navigator.onLine) return
	for (const { provider, refs } of rooms.values()) {
		// Never undo an explicit permission denial with an automatic retry.
		if (!refs || !provider.shouldConnect) continue
		if (
			provider.wsconnected &&
			Date.now() - provider.wsLastMessageReceived > 30_000
		)
			provider.disconnect()
		if (!provider.wsconnected && !provider.wsconnecting) provider.connect()
	}
}

window.addEventListener("online", resumeRooms)
window.addEventListener("pageshow", resumeRooms)
window.addEventListener("focus", resumeRooms)
document.addEventListener("visibilitychange", resumeRooms)

if (import.meta.hot)
	import.meta.hot.dispose(() => {
		window.removeEventListener("online", resumeRooms)
		window.removeEventListener("pageshow", resumeRooms)
		window.removeEventListener("focus", resumeRooms)
		document.removeEventListener("visibilitychange", resumeRooms)
		for (const connection of rooms.values()) {
			connection.provider.destroy()
			connection.doc.destroy()
		}
		rooms.clear()
	})
